import React, { useEffect, useState } from "react";
import { Dialog, DialogContent } from "../components/ui/dialog";
import { ImageOff, Calendar } from "lucide-react";
import { api, fileUrl } from "../lib/api";
import { GALLERY_CATEGORIES } from "../data/content";

const GalleryPage = () => {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("All");
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const url = filter === "All" ? "/gallery" : `/gallery?category=${encodeURIComponent(filter)}`;
    api.get(url).then((r) => setItems(r.data)).catch(() => setItems([])).finally(() => setLoading(false));
  }, [filter]);

  return (
    <div>
      <section className="bg-[#0F2942] py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="text-xs uppercase tracking-widest font-semibold text-amber-400">Gallery</span>
          <h1 className="font-display mt-3 text-4xl sm:text-5xl font-extrabold text-white">Our Work in Pictures</h1>
          <p className="mt-4 text-slate-300 max-w-2xl">
            Real moments from our health camps, education drives and community events across rural Bihar.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        {/* Filter pills */}
        <div className="flex flex-wrap gap-3 mb-10">
          {GALLERY_CATEGORIES.map((cat) => (
            <button
              key={cat}
              onClick={() => setFilter(cat)}
              data-testid={`gallery-filter-${cat.toLowerCase().replace(/\s+/g, "-")}`}
              className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all ${
                filter === cat
                  ? "bg-emerald-600 text-white shadow-md"
                  : "bg-white text-slate-600 border border-slate-200 hover:border-emerald-300 hover:text-emerald-700"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="aspect-[4/3] rounded-xl bg-slate-100 animate-pulse" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-24 text-slate-400">
            <ImageOff className="h-14 w-14 mx-auto mb-4" />
            <p className="font-medium">No photos in this category yet.</p>
            <p className="text-sm">Check back soon — new moments are added regularly.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {items.map((g) => (
              <button
                key={g.id}
                onClick={() => setActive(g)}
                data-testid={`gallery-item-${g.id}`}
                className="gallery-card group relative rounded-xl overflow-hidden aspect-[4/3] text-left"
              >
                <img src={fileUrl(g.image_path)} alt={g.event_name} className="w-full h-full object-cover gallery-img" />
                <span className="absolute top-3 left-3 bg-white/90 backdrop-blur text-slate-800 text-[11px] font-semibold px-3 py-1 rounded-full">
                  {g.category}
                </span>
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                  <p className="text-white font-semibold text-sm">{g.event_name}</p>
                  <p className="text-slate-300 text-xs flex items-center gap-1 mt-0.5">
                    <Calendar className="h-3 w-3" /> {g.date}
                  </p>
                </div>
              </button>
            ))}
          </div>
        )}
      </section>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-3xl p-0 overflow-hidden">
          {active && (
            <div>
              <img src={fileUrl(active.image_path)} alt={active.event_name} className="w-full max-h-[70vh] object-contain bg-black" />
              <div className="p-5">
                <span className="text-xs font-semibold text-emerald-700 uppercase tracking-wide">{active.category}</span>
                <h3 className="font-display text-xl font-bold text-slate-900 mt-1">{active.event_name}</h3>
                <p className="text-sm text-slate-400 flex items-center gap-1 mt-1">
                  <Calendar className="h-3.5 w-3.5" /> {active.date}
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GalleryPage;
